import axios from 'services/interceptor/Interceptor';
import Config from 'services/config/config';

const UPLOAD_URL = Config.fileUpload.documentUploadForm;

/**
 * Get file extension from name or MIME type.
 * @param {File} file
 * @returns {string}
 */
function getExtension(file) {
  if (!file) return '';
  const name = file.name || '';
  const lastDot = name.lastIndexOf('.');
  if (lastDot !== -1 && lastDot < name.length - 1) {
    return name.slice(lastDot + 1).toLowerCase();
  }
  const mime = file.type || '';
  const mimeMap = {
    'image/jpeg': 'jpg',
    'image/png': 'png',
    'image/gif': 'gif',
    'image/webp': 'webp',
    'application/pdf': 'pdf',
    'text/csv': 'csv',
  };
  return mimeMap[mime] || '';
}

/**
 * Infer doc_type from extension or MIME.
 * @param {string} extension
 * @param {File} file
 * @returns {string}
 */
function getDocType(extension, file) {
  const imageExtensions = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg'];
  if (imageExtensions.includes(extension?.toLowerCase())) return 'image';
  if (file?.type?.startsWith('image/')) return 'image';
  if (['pdf', 'doc', 'docx'].includes(extension?.toLowerCase())) return 'document';
  if (extension === 'csv' || file?.type === 'text/csv') return 'document';
  return 'document';
}

/**
 * Upload a file to the document upload API (multipart form).
 * Uses client-id, client-secret (from interceptor) and optional x-api-key from env.
 * Returns the uploaded file URL from the response.
 *
 * @param {File} file - The file to upload
 * @param {Object} options - Optional form fields (overrides defaults)
 * @param {string} [options.doc_name] - Document name (default: filename + timestamp)
 * @param {string} [options.service_name] - Service name (default: 'carnival')
 * @param {string} [options.extension] - File extension (default: from file name)
 * @param {string|boolean} [options.is_public] - (default: 'true')
 * @param {string} [options.doc_type] - e.g. 'image' | 'document' (default: inferred)
 * @param {string|boolean} [options.is_view_only] - (default: 'true')
 * @returns {Promise<string>} Resolved with the uploaded file URL
 * @throws {Error} On upload failure or when response does not contain a URL
 */
export async function uploadDocumentForm(file, options = {}) {
  if (!file || !(file instanceof File)) {
    throw new Error('uploadDocumentForm requires a File object');
  }

  const extension = options.extension ?? getExtension(file);
  const docType = options.doc_type ?? getDocType(extension, file);
  const docName =
    options.doc_name ??
    `${(file.name || 'file').replace(/\.[^/.]+$/, '')}_${Date.now()}`;
  const serviceName = options.service_name ?? 'carnival';
  const isPublic = options.is_public !== undefined ? String(options.is_public) : 'true';
  const isViewOnly = options.is_view_only !== undefined ? String(options.is_view_only) : 'true';

  const form = new FormData();
  form.append('file', file);
  form.append('doc_name', docName);
  form.append('service_name', serviceName);
  form.append('extension', extension || '');
  form.append('is_public', isPublic);
  form.append('doc_type', docType);
  form.append('is_view_only', isViewOnly);

  const config = { headers: {} };
  const apiKey = process.env.REACT_APP_DOCUMENT_UPLOAD_API_KEY;
  if (apiKey) config.headers['x-api-key'] = apiKey;
  if (process.env.REACT_APP_CLIENT_ID) config.headers['client-id'] = process.env.REACT_APP_CLIENT_ID;
  if (process.env.REACT_APP_CLIENT_SECRET) config.headers['client-secret'] = process.env.REACT_APP_CLIENT_SECRET;

  const response = await axios.post(UPLOAD_URL, form, config);

  const data = response?.data;
  // API returns { message, data: { url } }
  const url =
    data?.data?.url ??
    data?.data?.file_url ??
    data?.url ??
    data?.file_url ??
    '';

  if (url) {
    return url;
  }
  throw new Error(
    data?.message || 'Document upload succeeded but no URL in response.'
  );
}

export { default as DocumentUploadButton } from './DocumentUploadButton';
export default uploadDocumentForm;
